import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2, Send, Copy, Check, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog to send a quote to the end customer via Email + WhatsApp.
 * Renders a two-step flow:
 *   1) email/phone + channel toggles
 *   2) result summary + copyable public URL
 */
export function SendQuoteDialog({ open, onOpenChange, quote, onSent }) {
  const [emailAddr, setEmailAddr] = useState(quote?.customer_email || "");
  const [phone, setPhone] = useState(quote?.customer_phone || "");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendWa, setSendWa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setResult(null);
    setCopied(false);
    setEmailAddr(quote?.customer_email || "");
    setPhone(quote?.customer_phone || "");
    setSendEmail(true);
    setSendWa(false);
  };

  const submit = async () => {
    const channels = [];
    if (sendEmail) channels.push("email");
    if (sendWa) channels.push("whatsapp");
    if (channels.length === 0) {
      toast.error("Seleziona almeno un canale (email o WhatsApp)");
      return;
    }
    if (sendEmail && !emailAddr.trim()) {
      toast.error("Inserisci l'email del cliente");
      return;
    }
    if (sendWa && !phone.trim()) {
      toast.error("Inserisci il numero WhatsApp del cliente (con prefisso internazionale)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`, {
        channels,
        customer_email: sendEmail ? emailAddr.trim() : null,
        customer_phone: sendWa ? phone.trim().replace(/^\+/, "") : null,
        origin_url: window.location.origin,
      });
      setResult(data);
      if (data.email?.sent && data.whatsapp?.sent) {
        toast.success("Preventivo inviato via email e WhatsApp");
      } else if (data.email?.sent) {
        toast.success("Preventivo inviato via email");
      } else if (data.whatsapp?.sent) {
        toast.success("Preventivo inviato via WhatsApp");
      } else {
        toast.error("Invio fallito su tutti i canali — controlla i dettagli");
      }
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.public_url) return;
    try {
      await navigator.clipboard.writeText(result.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Invia preventivo al cliente</DialogTitle>
          <DialogDescription>
            {quote?.quote_number} · Totale € {(quote?.total || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Canali di invio</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <label className={`flex items-center gap-2 rounded-sm border px-3 py-2 cursor-pointer text-sm ${sendEmail ? "border-brand bg-brand-muted/30" : "border-zinc-200"}`}>
                  <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="send-quote-channel-email" />
                  <Mail className="h-3.5 w-3.5 text-brand" /> Email + PDF
                </label>
                <label className={`flex items-center gap-2 rounded-sm border px-3 py-2 cursor-pointer text-sm ${sendWa ? "border-brand bg-brand-muted/30" : "border-zinc-200"}`}>
                  <input type="checkbox" checked={sendWa} onChange={(e) => setSendWa(e.target.checked)} data-testid="send-quote-channel-whatsapp" />
                  <MessageCircle className="h-3.5 w-3.5 text-brand" /> WhatsApp
                </label>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1.5">
                L'email invia il PDF in allegato + link pagina pubblica. WhatsApp invia solo il link.
              </div>
            </div>

            {sendEmail && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Email cliente</Label>
                <Input
                  type="email"
                  value={emailAddr}
                  onChange={(e) => setEmailAddr(e.target.value)}
                  placeholder="cliente@esempio.it"
                  className="mt-1 rounded-sm"
                  data-testid="send-quote-email"
                />
              </div>
            )}

            {sendWa && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Telefono WhatsApp (con prefisso internazionale)
                </Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="es. 393331234567"
                  className="mt-1 rounded-sm font-mono"
                  data-testid="send-quote-phone"
                />
                <div className="text-[11px] text-zinc-500 mt-1">Formato E.164 senza + iniziale (es. 393331234567 per Italia).</div>
                <div className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-sm p-2 mt-2">
                  ⚠️ <b>Regola Meta 24h</b>: WhatsApp accetta messaggi liberi solo se il cliente ti ha scritto negli ultimi 24 ore.
                  Se il cliente non ti ha mai scritto o è passato oltre un giorno dall'ultima interazione,
                  il messaggio non verrà consegnato — in quel caso invia il preventivo via email.
                </div>
              </div>
            )}

            <div className="text-[11px] text-zinc-500 bg-zinc-50 p-3 rounded-sm">
              Il cliente riceverà un link pubblico dove potrà visionare il preventivo e pagare con Stripe (carte, Apple/Google Pay).
              Il link scade in 30 giorni. Al pagamento il preventivo viene marcato come "pagato" e viene aggiunta una nota al ticket.
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-medium mb-1">Riepilogo invio</div>
              {result.email && (
                <div className="text-sm text-emerald-900" data-testid="send-quote-result-email">
                  {result.email.sent ? "✅" : "❌"} Email → {result.email.to}
                  {result.email.error && <span className="text-red-700"> · {result.email.error}</span>}
                </div>
              )}
              {result.whatsapp && (
                <div className="text-sm text-emerald-900 mt-1" data-testid="send-quote-result-whatsapp">
                  {result.whatsapp.sent ? "✅" : "❌"} WhatsApp → +{result.whatsapp.to}
                  {result.whatsapp.error && <span className="text-red-700"> · {result.whatsapp.error}</span>}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">URL pubblico</Label>
              <div className="flex gap-2 mt-1">
                <Input value={result.public_url} readOnly className="rounded-sm font-mono text-xs" data-testid="send-quote-public-url" />
                <Button variant="outline" className="rounded-sm shrink-0" onClick={copyLink} data-testid="send-quote-copy-btn">
                  {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copiato</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copia</>}
                </Button>
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Scade il {new Date(result.expires_at).toLocaleDateString("it-IT")}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy}>
                Annulla
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-brand hover:bg-brand-dark text-white rounded-sm"
                data-testid="send-quote-submit"
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio...</> : <><Send className="h-4 w-4 mr-2" /> Invia al cliente</>}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
              Chiudi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
